import { Colors } from "../../../styles/theme";
import { Button, Dialog, DialogActions, DialogContent, Grid, capitalize, Box, Typography, Divider, Alert } from "@mui/material";
import GeneralInfoCard from "./generalInfoCard";
import AddressInfoCard from "./addressInfoCard";
import AdditionalInfoCard from "./additionalInfoCard";
import CommentsCard from "./commentsCard";
import { useFormik } from "formik";
import "yup-phone-lite";
import { CustomerService } from "../../../service/customerService";
import { UI_ACTION_TYPE, useUIContext } from "../../../state/ui";
import { isLocalDev } from "../../../utils/environment";
import { toast } from "react-toastify";
import AppDialogTitle from "../../ui/appDialogTitle";
import { customerModel } from "../model/profileModel";
import { validationSchema } from "../model/validationSchema";
import SearchCustomer from "../searchCustomer";
import { useState } from "react";
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkIcon from '@mui/icons-material/Link';
import FamilyMergeConfirmationDialog from "./familyMergeConfirmationDialog";

export default function LinkFamilyMemberDialog({
    open,
    onClose,
    onDone,
    linkTo  
}) {

    const { dispatch } = useUIContext();
    const [selectedExistingCustomer, setSelectedExistingCustomer] = useState(null);
    const [mode, setMode] = useState('search'); // 'search' or 'create'
    const [mergeConfirmationOpen, setMergeConfirmationOpen] = useState(false);

    // for the phonenumber parsing have to add country code e.g. +1 for US/CAD
    // parsePhoneNumber('+14162221111').format('NATIONAL')
    const formik = useFormik({
        initialValues: {
            ...customerModel
        },
        initialStatus: 'start',
        validationSchema: validationSchema,
        onSubmit: async (formData) => {            

            dispatch({loading: true, type: UI_ACTION_TYPE.LOADING,loadingtext: "Creating record...Please wait" });
            try {
                const customerServiceResponse = await CustomerService.linkfamily(linkTo.id, formData);
                const {
                    code,
                    data,
                    message
                } = {...customerServiceResponse};
                dispatch({loading: false, type: UI_ACTION_TYPE.LOADING});
                if( code === 0 ) {
                    toast.success(message);
                    onDone(data);
                    onClose();
                }
                else {
                    toast.error(message);
                }
            } catch (error) {
                if( isLocalDev )
                    console.log(error);

                dispatch({loading: false, type: UI_ACTION_TYPE.LOADING});
                toast.error(error.data.message);

            }
        }
    });

    const handleCustomerSelect = (customer) => {
        setSelectedExistingCustomer(customer);
        formik.setValues(customer);
        setMode('search'); // Stay in search mode when customer is selected
    }

    const handleLinkExistingClick = () => {
        // Check if we need to show merge confirmation
        const linkToHasFamily = linkTo.familyGroupId !== null && linkTo.familyGroupId !== undefined;
        const selectedHasFamily = selectedExistingCustomer.familyGroupId !== null && selectedExistingCustomer.familyGroupId !== undefined;
        const isMergingTwoFamilies = linkToHasFamily && selectedHasFamily && linkTo.familyGroupId !== selectedExistingCustomer.familyGroupId;

        if (isMergingTwoFamilies) {
            setMergeConfirmationOpen(true);
        } else {
            handleLinkExisting();
        }
    }

    const handleLinkExisting = async () => {
        if (!selectedExistingCustomer) return;
        
        dispatch({loading: true, type: UI_ACTION_TYPE.LOADING, loadingtext: "Linking family member...Please wait" });
        try {
            const customerServiceResponse = await CustomerService.linkfamily(linkTo.id, selectedExistingCustomer);
            const { code, data, message } = {...customerServiceResponse};
            dispatch({loading: false, type: UI_ACTION_TYPE.LOADING});
            
            if( code === 0 ) {
                toast.success(message);
                onDone(data);
                onClose();
            } else {
                toast.error(message);
            }
        } catch (error) {
            if( isLocalDev ) console.log(error);
            dispatch({loading: false, type: UI_ACTION_TYPE.LOADING});
            toast.error(error.data?.message || 'Error linking family member');
        } finally {
            setMergeConfirmationOpen(false);
        }
    }

    const handleSwitchToCreate = () => {
        setMode('create');
        setSelectedExistingCustomer(null);
        formik.resetForm();
    }

    const handleSwitchToSearch = () => {
        setMode('search');
        setSelectedExistingCustomer(null);
        formik.resetForm();
    }

    return (
      <>
        <Dialog onClose={onClose} open={open} fullWidth maxWidth={"xl"}>
          <AppDialogTitle title={`Link Family Member to ${capitalize(linkTo.fullName)}`} onClose={onClose} />

          <DialogContent sx={{ background: Colors.dove_gray }}>
            <Grid container pt={1} columnSpacing={1} rowSpacing={0}>
              <Grid item xs={6}>
                <Grid container rowSpacing={1}>
                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'background.paper', 
                      borderRadius: 1,
                      border: mode === 'search' ? '2px solid' : '1px solid',
                      borderColor: mode === 'search' ? 'primary.main' : 'divider'
                    }}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <LinkIcon color={mode === 'search' ? 'primary' : 'action'} />
                        <Typography variant="h6">Link Existing Customer</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Search for an existing customer to link as a family member
                      </Typography>
                      <SearchCustomer filterCustomer={linkTo} onDone={handleCustomerSelect} />
                      
                      {selectedExistingCustomer && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                          Selected: <strong>{selectedExistingCustomer.fullName}</strong>
                          {selectedExistingCustomer.familyGroupId && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              Already in a family group ({selectedExistingCustomer.familySize} member{selectedExistingCustomer.familySize !== 1 ? 's' : ''})
                            </Typography>
                          )}
                        </Alert>
                      )}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                      <Typography variant="body2" color="text.secondary">OR</Typography>
                    </Divider>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'background.paper', 
                      borderRadius: 1,
                      border: mode === 'create' ? '2px solid' : '1px solid',
                      borderColor: mode === 'create' ? 'primary.main' : 'divider'
                    }}>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <PersonAddIcon color={mode === 'create' ? 'primary' : 'action'} />
                        <Typography variant="h6">Create New Family Member</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Add a new customer and link them as a family member
                      </Typography>
                      <Button 
                        variant={mode === 'create' ? 'contained' : 'outlined'}
                        onClick={handleSwitchToCreate}
                        fullWidth
                      >
                        {mode === 'create' ? 'Creating New Member' : 'Create New Member'}
                      </Button>
                      {mode === 'search' && selectedExistingCustomer && (
                        <Button 
                          variant="text"
                          onClick={handleSwitchToSearch}
                          fullWidth
                          sx={{ mt: 1 }}
                          size="small"
                        >
                          Back to Link Existing
                        </Button>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              
              <Grid item xs={6}>
                <Grid container rowSpacing={1}>
                  {mode === 'create' ? (
                    <>
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 1 }}>
                          Fill in the details below to create and link a new family member
                        </Alert>
                      </Grid>
                      <Grid item xs={12}>
                        <GeneralInfoCard formik={formik} />
                      </Grid>
                      <Grid item xs={12}>
                        <AdditionalInfoCard formik={formik} />
                      </Grid>
                      <Grid item xs={12}>
                        <AddressInfoCard formik={formik} />
                      </Grid>
                      <Grid item xs={12}>
                        <CommentsCard formik={formik} />
                      </Grid>
                    </>
                  ) : (
                    <Grid item xs={12}>
                      {selectedExistingCustomer ? (
                        <>
                          <Alert severity="success" sx={{ mb: 2 }}>
                            Review the customer details below before linking
                          </Alert>
                          <GeneralInfoCard formik={formik} />
                          <Box mt={1}>
                            <AdditionalInfoCard formik={formik} />
                          </Box>
                          <Box mt={1}>
                            <AddressInfoCard formik={formik} />
                          </Box>
                        </>
                      ) : (
                        <Box 
                          sx={{ 
                            p: 4, 
                            textAlign: 'center',
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Customer Selected
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Search for an existing customer or create a new one to continue
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <>
              {mode === 'search' ? (
                <Button
                  disabled={!selectedExistingCustomer}
                  variant="contained"
                  autoFocus
                  type="button"
                  onClick={handleLinkExistingClick}
                  startIcon={<LinkIcon />}
                >
                  Link Family Member
                </Button>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={handleSwitchToSearch}
                    sx={{ mr: 'auto' }}
                  >
                    Back to Search
                  </Button>
                  <Button
                    disabled={!formik.isValid || !formik.dirty}
                    variant="contained"
                    autoFocus
                    type="button"
                    onClick={() => formik.submitForm()}
                    startIcon={<PersonAddIcon />}
                  >
                    Create & Link
                  </Button>
                </>
              )}
              <Button onClick={onClose} sx={{ color: Colors.muted }}>
                Cancel
              </Button>
            </>
          </DialogActions>
        </Dialog>

        <FamilyMergeConfirmationDialog
          open={mergeConfirmationOpen}
          onClose={() => {
            setMergeConfirmationOpen(false);
          }}
          primaryCustomer={linkTo}
          selectedCustomer={selectedExistingCustomer}
          onConfirm={handleLinkExisting}
          isLoading={false}
        />
      </>
    );
}